import React, { useEffect, useRef, useState, useMemo, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Stack, Typography, Box, Button, CardContent, Theme } from '@mui/material';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';
import { useAppSelector } from 'redux/hooks/reduxHooks';
import { selectIsAuth } from 'redux/selectors/selectors';
import { CustomMuiIcon } from 'assets/images/icons/customMuiIcons';
import { IChallangeData } from 'shared/types/challenges.interface';

import { CARD_COMMON_BUTTONS } from 'shared/constants/buttons-constants';
import { TCardType, ECardTypes } from 'shared/types/card.interface';
import { CardUserInfo } from 'components/Card/CardUserInfo/CardUserInfo';
import { CardStatus } from 'components/Card/CardStatus/CardStatus';
import { useGetProfileQuery } from 'redux/api/user.api';
import { CardMenuOpenButton } from 'components/Card/CardMenuOpenButton/CardMenuOpenButton';
import { CardMainButton } from 'components/Card/CardMainButton/CardMainButton';
import { CardEventFeature } from 'components/Card/CardEventFeature/CardEventFeature';
import { IDebateData } from 'shared/types/debates.interface';
import { urls } from 'shared/constants/routing.constants';
import { useRole } from 'shared/hooks/useRole';
import { useDebateDateTime } from 'shared/hooks/useDebateDateTime';

interface ICardProps {
  type: TCardType;
  cardInfo: IChallangeData;
  debate?: IDebateData;
}

const arrowStyles = {
  fontSize: { lg: '48px', md: '28px', sm: '28px', xs: '28px' },
  maxHeight: { lg: '24px', md: '20px', sm: '20px', xs: '20px' },
};

export const Card: React.FunctionComponent<ICardProps> = memo(({ type, cardInfo, debate }) => {
  const [isTextOpen, setIsTextOpen] = useState(false);
  const [isCurrentDescriptionLong, setCurrentDescriptionLong] = useState(false);
  const isAuth = useAppSelector(selectIsAuth);
  const { data: profileData = null } = useGetProfileQuery(undefined, { skip: !isAuth });
  const cardDescriptionRef = useRef(null);
  const navigate = useNavigate();

  const eventDateAndTime = useDebateDateTime(cardInfo.start_datetime);

  const userRole = useRole(cardInfo?.initiator.id, cardInfo?.opponent.id, profileData?.id);

  const shortTextStyles = useMemo(() => {
    if (!isTextOpen) {
      return {
        WebkitLineClamp: '2',
        MozLineClamp: '2',
        MsLineClamp: '2',
        lineClamp: '2',
      };
    }
    return {
      WebkitLineClamp: '6',
      MozLineClamp: '6',
      MsLineClamp: '6',
      lineClamp: '6',
    };
  }, [isTextOpen]);

  useEffect(() => {
    if (cardDescriptionRef.current && cardInfo) {
      if (cardDescriptionRef.current?.offsetHeight > 36) {
        setCurrentDescriptionLong(true);
      } else {
        setCurrentDescriptionLong(false);
      }
    }
  }, [cardInfo]);

  const handleCardClick = () => {
    if (debate) {
      navigate(`${urls.commonPages.debateCard}/${debate.id}`);
    } else {
      navigate(`${urls.commonPages.challengeCard}/${cardInfo.id}`);
    }
  };

  return (
    <Box
      position="relative"
      sx={{
        width: { lg: '350px', md: '331px', sm: '331px', xs: '331px' },
        minHeight: { lg: '475px', md: '411px', sm: '411px', xs: '411px' },
      }}
    >
      <CardContent
        sx={(theme: Theme) => ({
          minHeight: { lg: '475px', md: '411px', sm: '411px', xs: '411px' },
          padding: '24px',
          overflow: 'initial',
          position: isTextOpen ? 'absolute' : 'static',
          zIndex: isTextOpen ? 2 : 1,
          borderRadius: '12px',
          boxShadow: `-1px 1px 10px 0 ${theme.palette.myShadow.cardLight}`,
          transition: 'box-shadow 0.3s ease',
          width: { lg: '350px', md: '331px', sm: '331px', xs: '331px' },
          backgroundColor: theme.palette.primary.contrastText,
          cursor: 'pointer',
          '& .button': {
            opacity: { lg: 0, xs: 1 },
            transition: 'opacity 300ms ease-in-out',
          },
          '&:hover': {
            boxShadow: `0 4px 20px ${theme.palette.myShadow.cardDark}`,
            '& .button': {
              opacity: 1,
            },
          },
        })}
        onClick={handleCardClick}
      >
        <Stack spacing={0} direction="column" minHeight="100%">
          <Stack
            direction="row"
            justifyContent="space-between"
            width="100%"
            alignItems="flex-start"
          >
            <CardEventFeature
              icon={
                <DateRangeOutlinedIcon
                  sx={{
                    color: 'myGrey.grey600',
                    fontSize: { lg: 24, md: 20, sm: 20, xs: 20 },
                  }}
                />
              }
              title={eventDateAndTime.date}
              subtitle={eventDateAndTime.time}
            />
            <Stack
              direction="row"
              alignItems="center"
              sx={{ columnGap: { lg: '8px', md: '4px', sm: '4px', xs: '4px' } }}
              justifyContent="flex-end"
              onClick={(e) => e.stopPropagation()}
            >
              <CardStatus type={type} cardInfo={cardInfo} debate={debate} />
              <CardMenuOpenButton
                type={type}
                userRole={userRole}
                status={type === ECardTypes.Challenge ? cardInfo?.status : debate?.status}
                id={type === ECardTypes.Challenge ? cardInfo?.id : debate?.id}
              />
            </Stack>
          </Stack>
          <Box
            position="relative"
            sx={{ minHeight: { lg: '125px', md: '107px', sm: '107px', xs: '107px' }, mt: '25px' }}
          >
            <Typography
              variant="h3"
              color="secondary.main"
              sx={{
                fontWeight: 700,
                fontSize: { lg: 24, md: 18, sm: 18, xs: 18 },
                textAlign: 'start',
                alignSelf: 'flex-start',
                overflowWrap: 'break-word',
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                MozBoxOrient: 'vertical',
                MsBoxOrient: 'vertical',
                boxOrient: 'vertical',
                overflow: 'hidden',
                WebkitLineClamp: '2',
                MozLineClamp: '2',
                MsLineClamp: '2',
                lineClamp: '2',
              }}
            >
              {cardInfo?.thesis}
            </Typography>
            <Typography
              variant="caption"
              color="secondary.main"
              sx={{
                mt: { lg: '16px', md: '6px', sm: '6px', xs: '6px' },
                alignSelf: 'flex-start',
                maxWidth: '100%',
                overflowWrap: 'break-word',
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                MozBoxOrient: 'vertical',
                MsBoxOrient: 'vertical',
                boxOrient: 'vertical',
                overflow: 'hidden',
                ...shortTextStyles,
              }}
            >
              {cardInfo?.description}
            </Typography>
            <Typography
              ref={cardDescriptionRef}
              variant="caption"
              color="secondary.main"
              sx={{
                alignSelf: 'flex-start',
                maxWidth: '100%',
                position: 'absolute',
                overflowWrap: 'break-word',
                opacity: 0,
                zIndex: -1,
              }}
            >
              {cardInfo?.description}
            </Typography>
            {isCurrentDescriptionLong && (
              <Box onClick={(e) => e.stopPropagation()}>
                <Button
                  variant="link"
                  sx={(theme) => ({
                    color: theme.palette.info.light,
                    mt: { lg: '8px', md: '2px', sm: '2px', xs: '2px' },
                    maxHeight: '16px',
                    overflow: 'hidden',
                    fontSize: { md: 14, sm: 14, xs: 14 },
                  })}
                  onClick={() => setIsTextOpen((prev) => !prev)}
                  disableRipple
                >
                  {isTextOpen
                    ? CARD_COMMON_BUTTONS.buttonReadLess
                    : CARD_COMMON_BUTTONS.buttonReadMore}
                  <CustomMuiIcon type="icon-short-arrow-right" size="12px" />
                </Button>
              </Box>
            )}
          </Box>
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            sx={{
              gap: '8px',
              p: { lg: '27px 0', md: '24px 0', sm: '24px 0', xs: '24px 0' },
            }}
          >
            <CardUserInfo user={cardInfo?.initiator || null} />
            <Stack
              justifyContent="center"
              alignContent="center"
              alignSelf="flex-start"
              height="100%"
              position="relative"
              sx={{
                top: {
                  lg: cardInfo?.opponent.id ? '16px' : '28px',
                  md: cardInfo?.opponent.id ? '10px' : '20px',
                  sm: cardInfo?.opponent.id ? '10px' : '20px',
                  xs: cardInfo?.opponent.id ? '10px' : '20px',
                },
              }}
            >
              {cardInfo?.initiator && <CustomMuiIcon type="icon-arrow-right" sx={arrowStyles} />}
              {cardInfo?.opponent.id && <CustomMuiIcon type="icon-arrow-left" sx={arrowStyles} />}
            </Stack>
            <CardUserInfo user={cardInfo?.opponent || null} />
          </Stack>
          <Box onClick={(e) => e.stopPropagation()}>
            <CardMainButton
              type={type}
              userRole={userRole}
              status={type === ECardTypes.Challenge ? cardInfo?.status : debate?.status}
              challengeId={cardInfo.id}
              debate={debate}
            />
          </Box>
        </Stack>
      </CardContent>
    </Box>
  );
});

export default Card;
